import config from "config";

interface ProgressCallback {
  (progress: number): void;
}

interface XHROptions {
  method?: string;
  headers?: Record<string, string>;
  onProgress?: ProgressCallback;
}

/**
 * A failed upload. `status` is the HTTP status, or 0 when the request never got
 * a response. `message` is the API's own `error` string when it sent one, so
 * callers can show it to the person uploading rather than inventing their own.
 *
 * Reporting is left to the caller — it knows which field and form the upload
 * belongs to, and whether the failure is worth reporting at all.
 */
export class UploadError extends Error {
  readonly status: number;

  readonly errorCode?: string;

  readonly responseText?: string;

  constructor(
    message: string,
    status: number,
    errorCode?: string,
    responseText?: string,
  ) {
    super(message);
    this.name = "UploadError";
    this.status = status;
    this.errorCode = errorCode;
    this.responseText = responseText;
  }
}

const parseErrorBody = (
  responseText: string,
): { error?: string; error_code?: string } => {
  try {
    const body = JSON.parse(responseText);
    return typeof body === "object" && body !== null ? body : {};
  } catch {
    return {};
  }
};

export const xhrUpload = async <T>(
  endpoint: string,
  formData: FormData,
  options: XHROptions = {},
): Promise<T> => {
  const url = `${config.apiEndpoint}/api/v1${endpoint}`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Handle progress if callback provided
    if (options.onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          options.onProgress?.(progress);
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch {
          reject(
            new UploadError(
              "We could not read the server's response.",
              xhr.status,
              undefined,
              xhr.responseText,
            ),
          );
        }
        return;
      }

      const body = parseErrorBody(xhr.responseText);

      reject(
        new UploadError(
          body.error || `Upload failed with status ${xhr.status}`,
          xhr.status,
          body.error_code,
          xhr.responseText,
        ),
      );
    });

    xhr.addEventListener("error", () => {
      reject(new UploadError("Network error occurred", 0));
    });

    xhr.open(options.method || "POST", url);

    // Set credentials mode like in fetchPublicAPI
    xhr.withCredentials = true;

    // Apply headers from options
    const headers = {
      ...options.headers,
    };

    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });

    xhr.send(formData);
  });
};
